import { backend_api_v2 } from '@/utils/apiv2'
import { defineStore } from 'pinia'

// References that name more than one piece in a project
// (`constructibles/part_designs/ref_divergences/`, woodwall
// docs/ifc/part-ref-divergence-proposal.md).
//
// Never stored on the backend: a divergence belongs to the whole project at a
// given moment, and any panel's analysis can create or remove one. So the list
// is fetched fresh when a screen opens and again whenever an analysis may have
// changed it: every PENDING_RETRY_MS while the server says panels are still
// being analysed, and when the last pending design finishes
// (store_watchers/constructibles.js).

const PENDING_RETRY_MS = 30000

let retry_timer = null
let last_request = 0

export const useRefDivergencesStore = defineStore('ref_divergences', {
    state: () => ({
        project: null,  // the project uuid `data` belongs to
        data: null,     // the endpoint's answer
        loading: false,
    }),
    getters: {
        results: state => state.data?.results || [],
        count: state => state.data?.count || 0,
        // Panels of the project still being analysed. While it is not zero, a
        // divergence can be the middle of a re-upload rather than a problem.
        pending: state => state.data?.analysis_pending || 0,
        byRef: state => {
            const map = new Map()
            for (const result of state.data?.results || []) map.set(result.ref, result)
            return map
        },
    },
    actions: {
        load(project_uuid, { force = false } = {}) {
            if (!project_uuid || project_uuid === "all") {
                this.clear()
                return Promise.resolve(null)
            }
            if (!force && this.project === project_uuid && this.data) {
                return Promise.resolve(this.data)
            }
            if (this.project !== project_uuid) this.data = null
            this.project = project_uuid
            clearTimeout(retry_timer)

            const token = ++last_request
            this.loading = true
            return backend_api_v2
                .get(`constructibles/part_designs/ref_divergences/?project=${project_uuid}`)
                .then(({ data }) => {
                    // A newer request, or another project, has taken over.
                    if (token !== last_request) return null
                    this.data = data
                    if (data.analysis_pending > 0) {
                        retry_timer = setTimeout(
                            () => this.load(project_uuid, { force: true }), PENDING_RETRY_MS)
                    }
                    return data
                })
                .catch(e => {
                    // A warning layer: a failure hides the warnings, it never
                    // blocks the screen that shows them.
                    console.log(`[!] Can't load reference divergences: ${e}`)
                    return null
                })
                .finally(() => {
                    if (token === last_request) this.loading = false
                })
        },
        // Called when an analysis of this project may have changed the list.
        reloadIfLoaded(project_uuid) {
            if (this.project && this.project === project_uuid) {
                return this.load(project_uuid, { force: true })
            }
            return Promise.resolve(null)
        },
        clear() {
            clearTimeout(retry_timer)
            last_request++
            this.project = null
            this.data = null
            this.loading = false
        },
    },
})
